/**
 * The asset library browser — a small file manager over the folder the
 * agentContainer fills up while scraping product pages.
 *
 * This is the old "Scraped Assets" dialog: same tile grid, breadcrumbs, filter
 * and multi-select. What changed is the ending. It used to register the chosen
 * files as media records and hand back mongo ids; here it hands the entries
 * themselves back and the caller inlines their bytes into the snapshot, so
 * nothing is written anywhere but the snapshot file.
 *
 * Walks the folder one directory at a time. Selectable entries: image, video,
 * font, and bitmap-font pairs (`name.bm.png` + `name.bm.xml` as one tile);
 * anything else is listed greyed out so the folder still reads like a folder.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import FolderIcon from "@material-ui/icons/Folder";
import CreateNewFolderIcon from "@material-ui/icons/CreateNewFolder";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import FontDownloadIcon from "@material-ui/icons/FontDownload";
import VideocamIcon from "@material-ui/icons/Videocam";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import GetAppIcon from "@material-ui/icons/GetApp";
import _ from "lodash";
import {
  createAssetFolder,
  discardAssets,
  getAssetStaticUri,
  inspectAssets,
  listAssets,
  uploadAssetToLibrary,
} from "../../modules/snapshotEditorApi";
import { localFileToBase64 } from "../../modules/playEngineAssetUtils";
import { optimizeSourceForSnapshot } from "../../modules/snapshotOptimize";
import { downloadLibraryEntries } from "../../modules/snapshotLibraryDownload";
import {
  BAR_BG,
  CONTROL_BG,
  CONTROL_BORDER,
  FIELD_LABEL,
  PANEL_BG,
  SECTION_DIVIDER,
} from "./panelPalette";

// The panel's palette, plus one accent for the selected state — a tile has to
// stand out from the two greys around it to read as picked.
const TILE_SELECTED_BG = "#4a5a75";
const TILE_SELECTED_BORDER = "#90caf9";
// Ticked for discarding. Deliberately not the selection's blue: the two marks
// mean opposite things and sit on the same tile.
const TILE_MARKED_COLOR = "#ef9a9a";

const OUTLINED_BUTTON_STYLE = {
  color: FIELD_LABEL,
  backgroundColor: CONTROL_BG,
  border: `1px solid ${CONTROL_BORDER}`,
  textTransform: "none",
};

/** Asset types the library can actually provide; the rest live in the snapshot only. */
// Every type a snapshot can hold — mirrors SUPPORTED_TYPES on the server, so
// every asset row gets a Library button rather than a puzzling few.
export const LIBRARY_ASSET_TYPES = ["image", "audio", "video", "font", "bitmap-font", "atlas"];

const TYPE_BADGE = {
  image: { label: "image", color: "#90caf9" },
  video: { label: "video", color: "#ce93d8" },
  font: { label: "font", color: "#a5d6a7" },
  "bitmap-font": { label: "bitmap-font", color: "#ffcc80" },
};

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// ── Walking a Finder drop ────────────────────────────────────────────────────
//
// What a drop hands over is `DataTransferItem`s, not files: a dropped folder is
// a `FileSystemDirectoryEntry` that has to be read one page at a time and
// walked by hand to reach the files inside it. These three helpers do that,
// entirely client-side — nothing here touches the network.

function readDirectoryEntries(reader) {
  return new Promise((resolve, reject) => reader.readEntries(resolve, reject));
}

/** `readEntries` pages a large folder rather than returning it all at once. */
async function readAllDirectoryEntries(reader) {
  const all = [];
  let batch = await readDirectoryEntries(reader);
  while (batch.length > 0) {
    all.push(...batch);
    batch = await readDirectoryEntries(reader);
  }
  return all;
}

function readEntryFile(entry) {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

/**
 * Depth-first walk of one dropped entry, collecting every file and every
 * directory beneath it. `relDir` is "/"-joined and relative to the drop
 * itself — "" for something dropped directly, "sub" / "sub/deeper" for what a
 * dropped folder contained.
 */
async function walkEntry(entry, relDir, files, dirs) {
  // Finder/OS bookkeeping (`.DS_Store`, `.git`, …) rides along on a real drag
  // even though Finder itself never shows it — never part of the library,
  // same as `listAssets` already leaves it out of what it lists.
  if (entry.name && entry.name.startsWith(".")) return;

  if (entry.isFile) {
    const file = await readEntryFile(entry);
    files.push({ file, relDir });
    return;
  }
  if (!entry.isDirectory) return;

  const childRelDir = relDir ? `${relDir}/${entry.name}` : entry.name;
  dirs.add(childRelDir);
  const children = await readAllDirectoryEntries(entry.createReader());
  for (const child of children) {
    await walkEntry(child, childRelDir, files, dirs);
  }
}

/**
 * Mirrors the server's own `sanitizeFileName` (assetWrite.js) — a folder name
 * that carries a character the filesystem should not see (a space, an accent,
 * punctuation) comes back from `createAssetFolder` renamed, and everything
 * nested under it has to be aimed at that real name, not the one Finder used.
 * Only needed to predict the "already exists" case, where the server's plain
 * 409 carries no body to read the real name back from — a fresh create
 * reports its own `relativePath` and does not need this at all.
 */
function sanitizeLibrarySegment(rawName) {
  const base = String(rawName || "").trim();
  if (!base || base.startsWith(".")) return null;
  const cleaned = base.replace(/[^A-Za-z0-9._-]/g, "_");
  return cleaned && !cleaned.startsWith(".") ? cleaned : null;
}

/**
 * @param {FileSystemEntry[]} entries  top-level entries from the drop
 * @returns {Promise<{files: {file: File, relDir: string}[], dirs: Set<string>}>}
 *   `dirs` includes every folder in the tree, even an empty one, so it can be
 *   recreated in the library whether or not it carried any files.
 */
async function walkDroppedEntries(entries) {
  const files = [];
  const dirs = new Set();
  for (const entry of entries) {
    await walkEntry(entry, "", files, dirs);
  }
  return { files, dirs };
}

function EntryPreview({ entry }) {
  if (entry.assetType === "font") {
    return <FontDownloadIcon style={{ fontSize: 40, color: "#a5d6a7" }} />;
  }
  if (entry.assetType === "video") {
    if (entry.url) {
      return (
        <video
          src={getAssetStaticUri(entry.url)}
          muted
          preload="metadata"
          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
        />
      );
    }
    return <VideocamIcon style={{ fontSize: 40, color: "#ce93d8" }} />;
  }
  if (entry.url) {
    return (
      <img
        src={getAssetStaticUri(entry.url)}
        alt=""
        loading="lazy"
        style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
        onError={(e) => {
          e.target.style.display = "none";
        }}
      />
    );
  }
  return <InsertDriveFileIcon style={{ fontSize: 34, color: "#8a8d93" }} />;
}

EntryPreview.propTypes = {
  entry: PropTypes.object.isRequired,
};

/** One tile: folder, selectable asset, or greyed-out / incomplete file. */
function EntryTile({ entry, selectable, selected, marked, onOpen, onToggle, onMark }) {
  const isDir = entry.type === "dir";
  const badge = TYPE_BADGE[entry.assetType];

  return (
    <div
      onClick={() => (isDir ? onOpen(entry.relativePath) : selectable && onToggle(entry))}
      title={entry.name}
      style={{
        width: "132px",
        padding: "8px",
        borderRadius: "4px",
        backgroundColor: selected ? TILE_SELECTED_BG : BAR_BG,
        border: `1px solid ${
          marked ? TILE_MARKED_COLOR : selected ? TILE_SELECTED_BORDER : SECTION_DIVIDER
        }`,
        cursor: isDir || selectable ? "pointer" : "default",
        opacity: !isDir && !selectable ? 0.45 : 1,
        display: "flex",
        flexDirection: "column",
        gap: "6px",
        position: "relative",
      }}
    >
      {/* Ticked for discarding — a different question from the blue "picked to
          insert" state, and asked of EVERY tile: folders and the greyed-out
          files nothing can pick are exactly what a library needs cleaning of.
          Top-left, where the type badge is not. */}
      {onMark && (
        <IconButton
          size="small"
          title={marked ? "Not this one after all" : "Tick for discarding"}
          onClick={(event) => {
            // The tile itself opens the folder or picks the file; this does
            // neither.
            event.stopPropagation();
            onMark(entry);
          }}
          style={{
            position: "absolute",
            top: 2,
            left: 2,
            zIndex: 2,
            padding: "2px",
            color: marked ? TILE_MARKED_COLOR : "#cfd2d6",
            backgroundColor: "rgba(0,0,0,0.45)",
          }}
        >
          {marked ? (
            <CheckCircleIcon style={{ fontSize: 16 }} />
          ) : (
            <RadioButtonUncheckedIcon style={{ fontSize: 16 }} />
          )}
        </IconButton>
      )}

      {badge && (
        <span
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            fontSize: "9px",
            lineHeight: 1.2,
            padding: "2px 4px",
            borderRadius: "3px",
            backgroundColor: "#1e1e1e",
            color: badge.color,
            border: `1px solid ${badge.color}`,
            zIndex: 1,
          }}
        >
          {badge.label}
        </span>
      )}
      <div
        style={{
          height: "84px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: PANEL_BG,
          borderRadius: "3px",
          overflow: "hidden",
        }}
      >
        {isDir ? <FolderIcon style={{ fontSize: 40, color: "#c7a76c" }} /> : <EntryPreview entry={entry} />}
      </div>

      <Typography variant="caption" noWrap style={{ color: "#eee" }}>
        {entry.name}
      </Typography>
      <Typography variant="caption" style={{ color: "#8a8d93", fontSize: "10px" }}>
        {isDir ? "folder" : entry.incompletePair ? "incomplete pair" : formatSize(entry.sizeBytes)}
      </Typography>
    </div>
  );
}

EntryTile.propTypes = {
  entry: PropTypes.object.isRequired,
  selectable: PropTypes.bool,
  selected: PropTypes.bool,
  marked: PropTypes.bool,
  onOpen: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  // Absent for a read-only library: no tick is rendered at all.
  onMark: PropTypes.func,
};

/**
 * @param {string}   assetType  only entries of this type can be picked
 * @param {boolean}  multiple   several files at once (the Add flow) vs one
 * @param {Function} onChoose   async (entries) => void — awaited, so the dialog
 *                              can show progress while the bytes are read
 */
/**
 * The folder the library was last looking at.
 *
 * Module scope on purpose: it has to outlive the dialog (which is unmounted
 * every time it closes) and be shared by every button that opens one — the
 * Assets tab's Add and Replace, and Version Settings' Library. Browsing back to
 * the same deep folder for each asset is the tedium this removes.
 *
 * Deliberately NOT persisted: a reload starting at the root is fine, and a
 * remembered path that outlives the session is more likely to be stale than
 * useful.
 */
let lastDirPath = "";

export default function DialogAssetLibrary({
  baseID,
  assetType,
  multiple,
  canWrite = true,
  createSnackbar,
  onClose,
  onChoose,
  initialDirPath,
}) {
  const [dirPath, setDirPath] = useState("");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState({}); // relativePath -> entry
  const [working, setWorking] = useState(false);
  // The name being typed, or null when the folder row is closed — "" is a
  // legitimate state (the row is open and empty).
  const [newFolderName, setNewFolderName] = useState(null);
  const [progress, setProgress] = useState(null);
  // Ticked for discarding: a separate question from `selected`, which is what
  // will be inserted into the snapshot. Folders and unpickable files can be
  // ticked; neither can be selected.
  const [marked, setMarked] = useState({}); // relativePath -> entry
  // The confirmation. `counts` arrives only from the server's refusal for a
  // folder that is not empty — the second question, asked once for the batch.
  const [prompt, setPrompt] = useState(null); // { counts }
  // How many drag-enters are outstanding, not a boolean — a drag that crosses a
  // child element fires enter/leave on both, and a plain flag would flicker off
  // while still hovering the same drop zone.
  const [dragDepth, setDragDepth] = useState(0);
  const fileInputRef = useRef(null);
  // What openDirectory is currently showing, readable without making every
  // navigation rebuild the callback.
  const dirPathRef = useRef("");

  const openDirectory = useCallback(
    async (nextPath, { fallbackToRoot = false } = {}) => {
      setLoading(true);
      setFilter("");
      // A tick refers to a tile on screen, so it does not travel to another
      // folder — but it does survive a refresh of this one, which is what a
      // discard does to answer the "that folder is not empty" question.
      if ((nextPath || "") !== dirPathRef.current) setMarked({});

      const result = await listAssets(baseID, nextPath || "");
      if (!result.ok || _.get(result, "data.available") === false) {
        // A remembered folder that has since been renamed or removed answers
        // DIRECTORY_NOT_FOUND. That must not make the library unopenable, so
        // start over at the root instead of closing on the user.
        if (fallbackToRoot && nextPath) {
          lastDirPath = "";
          return openDirectory("");
        }
        const reason = result.ok ? _.get(result, "data.reason") : result.error;
        createSnackbar?.(`Asset library unavailable (${reason}).`, "warning", false);
        setLoading(false);
        onClose();
        return null;
      }

      const resolved = _.get(result, "data.dirPath", "");
      // Remember where we ended up, not where we asked to go — the server
      // normalises the path and this should follow it.
      lastDirPath = resolved;
      dirPathRef.current = resolved;
      const listed = _.get(result, "data.entries", []);
      setDirPath(resolved);
      setEntries(listed);
      setLoading(false);
      // Handed back as well as set: a caller that has just written into this
      // folder wants the fresh listing now, not on the next render.
      return listed;
    },
    [baseID, createSnackbar, onClose]
  );

  useEffect(() => {
    // A caller that knows which folder this pick concerns (the Assets tab,
    // reopening the library on the asset it is replacing) overrides the last
    // visit; otherwise reopen where that left off, wherever it was opened from.
    void openDirectory(initialDirPath ?? lastDirPath, { fallbackToRoot: true });
    // Opening once, on mount; later navigation goes through the tiles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSelectable = useCallback(
    (entry) =>
      entry.type === "file" && entry.selectable === true && (!assetType || entry.assetType === assetType),
    [assetType]
  );

  const toggleEntry = useCallback(
    (entry) => {
      setSelected((previous) => {
        if (previous[entry.relativePath]) {
          const next = { ...previous };
          delete next[entry.relativePath];
          return next;
        }
        // One slot takes one asset; picking a second replaces the first.
        return multiple
          ? { ...previous, [entry.relativePath]: entry }
          : { [entry.relativePath]: entry };
      });
    },
    [multiple]
  );

  const breadcrumbs = useMemo(() => {
    const parts = dirPath ? dirPath.split("/") : [];
    return [
      { label: "assets", path: "" },
      ...parts.map((part, index) => ({ label: part, path: parts.slice(0, index + 1).join("/") })),
    ];
  }, [dirPath]);

  const visibleEntries = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    if (!needle) return entries;
    return entries.filter((entry) => entry.name.toLowerCase().includes(needle));
  }, [entries, filter]);

  const selectedEntries = useMemo(() => Object.values(selected), [selected]);
  const markedEntries = useMemo(() => Object.values(marked), [marked]);

  /**
   * The confirmation, in full: one line per ticked tile saying what it actually
   * is, and one sentence of totals.
   *
   * Built from what the server just reported rather than from the listing, so a
   * folder reads "12 files in 3 subfolders" instead of "folder" — and so an
   * entry that has gone since the listing was drawn says so instead of failing
   * the whole batch when Discard is pressed.
   */
  const discardSummary = useMemo(() => {
    const inspected = _.keyBy(_.get(prompt, "inspected", []), "relativePath");

    const rows = markedEntries.map((entry) => {
      const parts = filesBehind(entry).map((path) => inspected[path]).filter(Boolean);
      const missing = parts.length > 0 && parts.every((part) => part.kind === "missing");
      const sizeBytes = _.sumBy(parts, (part) => part.sizeBytes || 0);

      let detail = "";
      if (missing) {
        detail = "already gone";
      } else if (entry.type === "dir") {
        const dir = parts.find((part) => part.kind === "dir") || { files: 0, folders: 0 };
        const subfolders = dir.folders === 1 ? "subfolder" : "subfolders";
        if (!dir.files) {
          // `files` counts the whole tree, so none of them means every one of
          // those subfolders is empty too — worth saying, because "0 files in 1
          // subfolder" is a riddle rather than an answer.
          detail = dir.folders ? `${dir.folders} empty ${subfolders}` : "empty";
        } else {
          detail = `${dir.files} file${dir.files === 1 ? "" : "s"}`;
          if (dir.folders) detail += ` in ${dir.folders} ${subfolders}`;
          if (sizeBytes) detail += ` · ${formatSize(sizeBytes)}`;
        }
      } else {
        detail = parts.length > 1 ? `pair · ${formatSize(sizeBytes)}` : formatSize(sizeBytes);
      }

      return { path: entry.relativePath, name: entry.name, missing, detail, sizeBytes };
    });

    const live = rows.filter((row) => !row.missing);
    const fileCount = markedEntries.reduce((sum, entry) => {
      const parts = filesBehind(entry).map((path) => inspected[path]).filter(Boolean);
      const dir = parts.find((part) => part.kind === "dir");
      return sum + (dir ? dir.files : parts.filter((part) => part.kind === "file").length);
    }, 0);
    const totalBytes = _.sumBy(live, (row) => row.sizeBytes || 0);

    const pieces = [];
    if (fileCount) pieces.push(`${fileCount} file${fileCount === 1 ? "" : "s"}`);
    if (totalBytes) pieces.push(formatSize(totalBytes));

    return {
      title: `Discard ${rows.length} item${rows.length === 1 ? "" : "s"}?`,
      rows,
      movable: live.length,
      total: pieces.length ? `${pieces.join(", ")} in all.` : "",
    };
  }, [prompt, markedEntries]);

  const selectAllHere = useCallback(() => {
    setSelected((previous) => {
      const next = { ...previous };
      visibleEntries.filter(isSelectable).forEach((entry) => {
        next[entry.relativePath] = entry;
      });
      return next;
    });
  }, [isSelectable, visibleEntries]);

  /**
   * Make a folder here. The server sanitises the name; report back what it made.
   *
   * Opens straight into what was just made rather than refreshing this folder —
   * a folder is made to hold something, and re-clicking into it every time is
   * the tedium this removes.
   */
  async function createFolder() {
    const name = String(newFolderName || "").trim();
    if (!name) return;

    setWorking(true);
    try {
      const result = await createAssetFolder(baseID, { dirPath, name });
      if (!result.ok) {
        createSnackbar?.(`Could not create the folder (${result.error}).`, "error", false);
        return;
      }
      const created = _.get(result, "data.name", name);
      const createdPath = _.get(result, "data.relativePath", dirPath ? `${dirPath}/${created}` : created);
      setNewFolderName(null);
      createSnackbar?.(`Folder "${created}" created.`, "success", false);
      await openDirectory(createdPath);
    } finally {
      setWorking(false);
    }
  }

  /**
   * Read, optimise and upload one file into `targetDir`. A failure is reported
   * against the file it belongs to and answered with `null`, so a batch upload
   * can skip it and keep going rather than fail as a whole.
   */
  const uploadOneFile = useCallback(
    async (targetDir, file, optimizationStats) => {
      let source;
      try {
        source = await localFileToBase64(file);
      } catch (_err) {
        createSnackbar?.(`Could not read "${file.name}".`, "error", false);
        return null;
      }

      // Use the same size optimisation as snapshot-embedded uploads before
      // writing the library copy. `assetType` may be unset for the general
      // library, but the optimizer still only changes image data URLs.
      const compressedSource = await optimizeSourceForSnapshot({
        baseID,
        assetType: assetType || "image",
        source,
        fileName: file.name,
        onSaved: (stats) => optimizationStats.push(stats),
      });

      const result = await uploadAssetToLibrary(baseID, {
        dirPath: targetDir,
        fileName: file.name,
        source: compressedSource,
      });
      if (!result.ok) {
        createSnackbar?.(`"${file.name}" was not uploaded (${result.error}).`, "error", false);
        return null;
      }
      return _.get(result, "data.relativePath");
    },
    [assetType, baseID, createSnackbar]
  );

  /**
   * Select whatever just landed in the folder being browsed. Uploading here is
   * almost always "I want to use this one", so the fresh files come back
   * selected. A complete pair is listed under its shared stem (`logo.bm`), not
   * under either half, hence the prefix match. Files that landed deeper, inside
   * a dropped folder, are not selectable here at all — this folder's listing
   * does not contain them.
   */
  const selectJustUploaded = useCallback(
    (listed, relativePaths) => {
      if (!relativePaths.length) return;
      const justUploaded = listed.filter(
        (entry) =>
          isSelectable(entry) &&
          relativePaths.some(
            (relativePath) =>
              relativePath === entry.relativePath ||
              String(relativePath).startsWith(`${entry.relativePath}.`)
          )
      );
      if (!justUploaded.length) return;

      setSelected((previous) => {
        if (!multiple) {
          const last = justUploaded[justUploaded.length - 1];
          return { [last.relativePath]: last };
        }
        const next = { ...previous };
        justUploaded.forEach((entry) => {
          next[entry.relativePath] = entry;
        });
        return next;
      });
    },
    [isSelectable, multiple]
  );

  /**
   * Put the picked files into the folder being browsed.
   *
   * One request per file rather than one big one: a 20-file drop should not be
   * all-or-nothing, and a single refusal (an unsupported type, a name the server
   * would not take) is reported against the file it belongs to while the rest
   * still land.
   */
  async function uploadFiles(files) {
    if (!files.length) return;

    setWorking(true);
    const uploaded = [];
    const optimizationStats = [];
    try {
      for (let index = 0; index < files.length; index += 1) {
        setProgress(`Uploading ${index + 1}/${files.length}…`);
        const relativePath = await uploadOneFile(dirPath, files[index], optimizationStats);
        if (relativePath) uploaded.push(relativePath);
      }
    } finally {
      setProgress(null);
      setWorking(false);
    }

    if (!uploaded.length) return;
    let uploadMessage =
      `${uploaded.length} file${uploaded.length === 1 ? "" : "s"} added to ${dirPath || "assets"}.`;
    if (optimizationStats.length) {
      const originalSize = optimizationStats.reduce((sum, stats) => sum + stats.originalSize, 0);
      const optimizedSize = optimizationStats.reduce((sum, stats) => sum + stats.compressedSize, 0);
      const savedPercent = originalSize > 0
        ? ((originalSize - optimizedSize) / originalSize * 100).toFixed(1)
        : "0";
      uploadMessage += ` Images optimized by ${savedPercent}% with Smart Auto Optimize.`;
    }
    createSnackbar?.(uploadMessage, "success", false);

    const listed = (await openDirectory(dirPath)) || [];
    selectJustUploaded(listed, uploaded);
  }

  /**
   * Put a dropped file/folder tree into the folder being browsed, recreating
   * whatever subfolders it carried.
   *
   * Folders first, shallowest to deepest, so a nested one always has its parent
   * by the time it is made; an existing folder (the drop merges into one already
   * there) is not an error. Files follow, each into the subfolder it was dropped
   * in — a loose file dropped directly on the browser lands right here, exactly
   * like "Upload Here".
   */
  async function uploadDroppedEntries(entries) {
    setWorking(true);
    let files;
    let dirs;
    try {
      ({ files, dirs } = await walkDroppedEntries(entries));
    } catch (err) {
      createSnackbar?.(`Could not read the dropped items (${err.message}).`, "error", false);
      setWorking(false);
      return;
    }

    if (!files.length) {
      setWorking(false);
      createSnackbar?.("Nothing to upload — the drop had no files.", "warning", false);
      return;
    }

    // Dropped-tree relDir ("" for the root) → its REAL folder in the library.
    // A fresh `createAssetFolder` reports that name back directly; an existing
    // one (409, no body) falls back to predicting it, since a folder Finder
    // already agrees with the server about sanitises to the same name every
    // time. Either way, everything below only ever targets a path the server
    // has actually confirmed — never the raw name Finder used.
    const resolvedDir = new Map([["", dirPath]]);
    const orderedDirs = Array.from(dirs).sort((a, b) => a.split("/").length - b.split("/").length);
    for (const relDir of orderedDirs) {
      const splitAt = relDir.lastIndexOf("/");
      const parentRelDir = splitAt === -1 ? "" : relDir.slice(0, splitAt);
      const rawName = splitAt === -1 ? relDir : relDir.slice(splitAt + 1);
      const targetParent = resolvedDir.get(parentRelDir);
      // The parent itself never resolved (its own create failed) — nothing
      // under it can be reached either, so there is nothing useful to ask for.
      if (targetParent === undefined) continue;

      const result = await createAssetFolder(baseID, { dirPath: targetParent, name: rawName });
      if (result.ok) {
        resolvedDir.set(relDir, _.get(result, "data.relativePath"));
      } else if (result.error === "FOLDER_ALREADY_EXISTS") {
        const sanitized = sanitizeLibrarySegment(rawName);
        if (sanitized) {
          resolvedDir.set(relDir, targetParent ? `${targetParent}/${sanitized}` : sanitized);
        }
      } else {
        createSnackbar?.(`Could not create folder "${rawName}" (${result.error}).`, "error", false);
      }
    }

    const uploaded = []; // { relDir, relativePath }
    const skippedForFolder = [];
    const optimizationStats = [];
    try {
      for (let index = 0; index < files.length; index += 1) {
        const { file, relDir } = files[index];
        setProgress(`Uploading ${index + 1}/${files.length}…`);
        const targetDir = resolvedDir.get(relDir);
        if (targetDir === undefined) {
          skippedForFolder.push(file.name);
          continue;
        }
        const relativePath = await uploadOneFile(targetDir, file, optimizationStats);
        if (relativePath) uploaded.push({ relDir, relativePath });
      }
    } finally {
      setProgress(null);
    }

    if (skippedForFolder.length) {
      createSnackbar?.(
        `${skippedForFolder.length} file${skippedForFolder.length === 1 ? "" : "s"} skipped — ` +
          "the folder to hold them could not be created.",
        "error",
        false
      );
    }

    setWorking(false);
    if (!uploaded.length) return;

    const folderCount = uploaded.filter((entry) => entry.relDir).length;
    let message = `${uploaded.length} file${uploaded.length === 1 ? "" : "s"} added`;
    message += folderCount ? ` (${folderCount} inside dropped folders).` : ` to ${dirPath || "assets"}.`;
    createSnackbar?.(message, "success", false);

    const listed = (await openDirectory(dirPath)) || [];
    const topLevel = uploaded.filter((entry) => !entry.relDir).map((entry) => entry.relativePath);
    selectJustUploaded(listed, topLevel);
  }

  const handleDragEnter = useCallback(
    (event) => {
      event.preventDefault();
      if (!canWrite || working) return;
      setDragDepth((depth) => depth + 1);
    },
    [canWrite, working]
  );

  const handleDragOver = useCallback(
    (event) => {
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = canWrite && !working ? "copy" : "none";
    },
    [canWrite, working]
  );

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    setDragDepth((depth) => Math.max(0, depth - 1));
  }, []);

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      setDragDepth(0);
      if (!canWrite || working) return;

      // `webkitGetAsEntry` has to be called synchronously, in this handler,
      // before anything here awaits — some browsers invalidate the transfer's
      // items once the event has finished dispatching.
      const items = event.dataTransfer?.items;
      const fileEntries = items && items.length
        ? Array.from(items)
          .map((item) => (typeof item.webkitGetAsEntry === "function" ? item.webkitGetAsEntry() : null))
          .filter(Boolean)
        : [];

      if (fileEntries.length) {
        void uploadDroppedEntries(fileEntries);
        return;
      }

      // No entry API (older Safari, or a non-Chromium browser): still take a
      // flat drop of files, just without any folder structure to recreate.
      const flatFiles = Array.from(event.dataTransfer?.files || []);
      if (flatFiles.length) void uploadFiles(flatFiles);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canWrite, working, dirPath, baseID]
  );

  /**
   * The files behind one tile. A bitmap font or an atlas is ONE entry here and
   * two files on disk, and moving only one half would leave the library with
   * the orphan the lister greys out.
   */
  function filesBehind(entry) {
    if (entry.type === "dir") return [entry.relativePath];
    const halves = _.uniq(_.compact([entry.imagePath, entry.metaPath]));
    return halves.length ? halves : [entry.relativePath];
  }

  const toggleMark = useCallback((entry) => {
    setMarked((previous) => {
      if (previous[entry.relativePath]) return _.omit(previous, entry.relativePath);
      return { ...previous, [entry.relativePath]: entry };
    });
  }, []);

  /**
   * Ask the server what is behind the ticked tiles, then ask the user once.
   *
   * The listing on screen shows one directory, so nothing here knows what is
   * nested inside a ticked folder — and that is exactly what the person
   * deciding needs to see. Asking first means ONE dialog that already knows the
   * answer, instead of a confirmation that turns into a second confirmation as
   * soon as the server has an objection.
   */
  async function openDiscardPrompt() {
    const entries = Object.values(marked);
    if (!entries.length) return;

    setWorking(true);
    try {
      const result = await inspectAssets(baseID, entries.flatMap(filesBehind));
      if (!result.ok) {
        const code = _.get(result, "error.error") || result.error;
        createSnackbar?.(`Could not read those entries (${code}).`, "error", false);
        return;
      }
      setPrompt({ inspected: _.get(result, "data.entries", []) });
    } finally {
      setWorking(false);
    }
  }

  /**
   * Move every ticked tile out of the library. NOTHING IS DELETED: the server
   * moves them into the library's `badones/` folder, which nothing here lists
   * or can reach.
   *
   * Files and folders go in two calls, because one request states one kind —
   * that is what lets the server refuse a tile whose listing has gone stale.
   * `recursive` is unconditional: the dialog that got us here has already said
   * what is inside, so a second question would be asking twice.
   */
  async function discardMarked() {
    // What the inspection found missing cannot be moved, and asking the server
    // to would fail the whole batch on the one entry nobody can do anything
    // about. Dropped here, reported in the dialog above.
    const gone = new Set(
      _.get(prompt, "inspected", [])
        .filter((entry) => entry.kind === "missing")
        .map((entry) => entry.relativePath)
    );
    const entries = Object.values(marked).filter(
      (entry) => !filesBehind(entry).every((path) => gone.has(path))
    );
    if (!entries.length) {
      setPrompt(null);
      setMarked({});
      await openDirectory(dirPath);
      return;
    }

    const files = entries.filter((entry) => entry.type !== "dir");
    const folders = entries.filter((entry) => entry.type === "dir");

    const fail = (result, what) => {
      const code = _.get(result, "error.error") || result.error;
      createSnackbar?.(`Could not discard ${what} (${code}).`, "error", false);
    };

    setWorking(true);
    setPrompt(null);
    try {
      if (files.length) {
        const result = await discardAssets(baseID, {
          paths: files.flatMap(filesBehind),
          expect: "file",
        });
        if (!result.ok) return fail(result, files.length === 1 ? `"${files[0].name}"` : "those files");
        // Gone, so out of both marks and selection — a picked file that has
        // left the folder would be fetched by "Use selected" and fail at the
        // last moment.
        const keys = files.flatMap((entry) => [entry.relativePath, ...filesBehind(entry)]);
        setMarked((previous) => _.omit(previous, keys));
        setSelected((previous) => _.omit(previous, keys));
      }

      if (folders.length) {
        const result = await discardAssets(baseID, {
          paths: folders.map((entry) => entry.relativePath),
          expect: "dir",
          recursive: true,
        });
        if (!result.ok) return fail(result, folders.length === 1 ? `"${folders[0].name}"` : "those folders");
        setMarked((previous) => _.omit(previous, folders.map((entry) => entry.relativePath)));
      }

      createSnackbar?.(
        `${entries.length} item${entries.length === 1 ? "" : "s"} moved to badones.`,
        "success",
        false
      );
    } finally {
      // Whatever happened, including a half-done batch, the listing is what
      // says what is actually there now.
      await openDirectory(dirPath);
      setWorking(false);
    }
  }

  async function confirm() {
    if (!selectedEntries.length) return;
    setWorking(true);
    try {
      await onChoose(selectedEntries);
      onClose();
    } catch (err) {
      console.error("[snapshotAssetLibrary] choose failed:", err);
      createSnackbar?.(`Could not read the asset: ${err.message}`, "error", false);
    } finally {
      setWorking(false);
    }
  }

  async function downloadMarked() {
    if (!markedEntries.length) return;
    setWorking(true);
    try {
      const result = await downloadLibraryEntries(baseID, markedEntries, setProgress);
      if (!result.count) {
        createSnackbar?.("The selection contains no downloadable assets.", "warning", false);
        return;
      }
      const skipped = result.skipped
        ? ` ${result.skipped} unrecognised file${result.skipped === 1 ? " was" : "s were"} skipped.`
        : "";
      createSnackbar?.(
        `Downloaded ${result.count} file${result.count === 1 ? "" : "s"}.${skipped}`,
        result.skipped ? "warning" : "success",
        false
      );
    } catch (err) {
      console.error("[snapshotAssetLibrary] download failed:", err);
      createSnackbar?.(`Could not download the selection: ${err.message}`, "error", false);
    } finally {
      setProgress(null);
      setWorking(false);
    }
  }

  return (
    <Dialog
      open
      onClose={working ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ style: { backgroundColor: PANEL_BG } }}
    >
      <DialogTitle style={{ paddingBottom: 4 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "12px" }}>
          <span>Asset Library</span>
          <Typography
            variant="caption"
            style={{ color: "#8a8d93", fontWeight: 400, textAlign: "right", maxWidth: "55%" }}
          >
            {assetType ? `Picking a ${assetType}` : `Supported: ${LIBRARY_ASSET_TYPES.join(", ")}`}
          </Typography>
        </div>
      </DialogTitle>
      <DialogContent
        style={{ minHeight: "60vh", position: "relative" }}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Covers the whole panel while something is dragged over it — dropping
            anywhere in here (not only on the tile grid) lands in the folder
            being browsed, folders included. Pointer events pass through so the
            drag itself is not disturbed by hovering the overlay. */}
        {canWrite && dragDepth > 0 && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(74, 90, 117, 0.35)",
              border: `2px dashed ${TILE_SELECTED_BORDER}`,
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <Typography style={{ color: "#fff", fontSize: "15px", fontWeight: 500 }}>
              Drop files or folders to add them to {dirPath || "assets"}
            </Typography>
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", flex: 1, minWidth: 0 }}>
            {breadcrumbs.map((crumb, index) => (
              <span key={crumb.path || "root"} style={{ display: "inline-flex", alignItems: "center" }}>
                {index > 0 && <span style={{ color: "#8a8d93", margin: "0 4px" }}>/</span>}
                <span
                  onClick={() => index < breadcrumbs.length - 1 && openDirectory(crumb.path)}
                  style={{
                    color: index === breadcrumbs.length - 1 ? "#eee" : TILE_SELECTED_BORDER,
                    cursor: index === breadcrumbs.length - 1 ? "default" : "pointer",
                    fontSize: "13px",
                  }}
                >
                  {crumb.label}
                </span>
              </span>
            ))}
          </div>
          {progress && (
            <Typography variant="caption" style={{ color: "#8a8d93", flexShrink: 0 }}>
              {progress}
            </Typography>
          )}

          {/* Actions on the library folder shown by the breadcrumbs. Download
              only reads it; the remaining buttons mutate it. */}
          {canWrite && (
            <>
              {/* Download and Discard act on what is ticked, so they sit with
                  the other library actions rather than with the picker's
                  actions in the footer.

                  It is styled by hand for the empty case as well as the full
                  one: OUTLINED_BUTTON_STYLE is an inline style, so it wins over
                  the class MUI greys a disabled button out with — leaving a
                  button that refuses to be pressed while looking like it would.  */}
              <Button
                size="small"
                variant="outlined"
                startIcon={<GetAppIcon style={{ fontSize: 16 }} />}
                onClick={() => void downloadMarked()}
                disabled={working || loading || markedEntries.length === 0}
                style={{
                  ...OUTLINED_BUTTON_STYLE,
                  flexShrink: 0,
                  ...(markedEntries.length
                    ? {}
                    : { color: "#6f7378", borderColor: SECTION_DIVIDER, opacity: 0.55 }),
                }}
              >
                Download{markedEntries.length ? ` (${markedEntries.length})` : ""}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => void openDiscardPrompt()}
                disabled={working || loading || markedEntries.length === 0}
                style={{
                  ...OUTLINED_BUTTON_STYLE,
                  flexShrink: 0,
                  ...(markedEntries.length
                    ? { color: TILE_MARKED_COLOR, borderColor: TILE_MARKED_COLOR }
                    : { color: "#6f7378", borderColor: SECTION_DIVIDER, opacity: 0.55 }),
                }}
              >
                Discard{markedEntries.length ? ` (${markedEntries.length})` : ""}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CreateNewFolderIcon style={{ fontSize: 16 }} />}
                onClick={() => setNewFolderName("")}
                disabled={working || loading || newFolderName !== null}
                style={{ ...OUTLINED_BUTTON_STYLE, flexShrink: 0 }}
              >
                New Folder
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CloudUploadIcon style={{ fontSize: 16 }} />}
                onClick={() => fileInputRef.current?.click()}
                disabled={working || loading}
                style={{ ...OUTLINED_BUTTON_STYLE, flexShrink: 0 }}
              >
                Upload Here
              </Button>
              {/* No `accept`: the library takes any file. What the engine
                  cannot use is stored all the same and listed greyed out, so a
                  brief or a source file can sit beside the assets it produced. */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={(e) => {
                  // Read the list out before clearing the input, so picking the
                  // same file twice in a row still fires a change event.
                  const files = Array.from(e.target.files || []);
                  e.target.value = "";
                  void uploadFiles(files);
                }}
              />
            </>
          )}

          <TextField
            size="small"
            variant="outlined"
            placeholder="Filter this folder…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            InputProps={{ style: { color: "#fff", fontSize: "13px" } }}
            style={{ width: "220px", flexShrink: 0 }}
          />
        </div>

        {newFolderName !== null && (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
            <TextField
              autoFocus
              size="small"
              variant="outlined"
              placeholder="Folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") void createFolder();
                if (e.key === "Escape") setNewFolderName(null);
              }}
              InputProps={{ style: { color: "#fff", fontSize: "13px" } }}
              style={{ width: "240px" }}
            />
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => void createFolder()}
              disabled={working || !newFolderName.trim()}
            >
              Create
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setNewFolderName(null)}
              disabled={working}
              style={OUTLINED_BUTTON_STYLE}
            >
              Cancel
            </Button>
            <Typography variant="caption" style={{ color: "#8a8d93" }}>
              inside {dirPath || "assets"}
            </Typography>
          </div>
        )}

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
            <CircularProgress size={28} />
          </div>
        ) : visibleEntries.length === 0 ? (
          <Typography variant="body2" style={{ color: "#8a8d93" }}>
            This folder is empty.
          </Typography>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
            {visibleEntries.map((entry) => (
              <EntryTile
                key={entry.relativePath}
                entry={entry}
                selectable={isSelectable(entry)}
                selected={Boolean(selected[entry.relativePath])}
                marked={Boolean(marked[entry.relativePath])}
                onOpen={openDirectory}
                onToggle={toggleEntry}
                onMark={canWrite && !working ? toggleMark : undefined}
              />
            ))}
          </div>
        )}
      </DialogContent>

      <DialogActions
        style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px 24px", flexWrap: "wrap" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0 }}>
          <Typography variant="body2" style={{ color: FIELD_LABEL }}>
            {selectedEntries.length} selected
          </Typography>
          {multiple && (
            <Button
              size="small"
              variant="outlined"
              onClick={selectAllHere}
              disabled={working}
              style={OUTLINED_BUTTON_STYLE}
            >
              Select All
            </Button>
          )}
          <Button
            size="small"
            variant="outlined"
            onClick={() => setSelected({})}
            disabled={working || !selectedEntries.length}
            style={OUTLINED_BUTTON_STYLE}
          >
            Clear
          </Button>
        </div>

        <Button
          size="small"
          variant="outlined"
          onClick={onClose}
          disabled={working}
          style={OUTLINED_BUTTON_STYLE}
        >
          Close
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={working || selectedEntries.length === 0}
          onClick={confirm}
        >
          {working ? "Adding…" : multiple ? "Add selected" : "Use selected"}
        </Button>
      </DialogActions>

      <Dialog
        open={Boolean(prompt)}
        onClose={working ? undefined : () => setPrompt(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { backgroundColor: PANEL_BG } }}
      >
        {prompt && (
          <>
            <DialogTitle style={{ paddingBottom: 4 }}>
              {discardSummary.title}
            </DialogTitle>
            <DialogContent>
              {/* One line per ticked tile, each saying what it actually is —
                  this is the whole reason the server is asked before the
                  question is put, rather than after it has been answered. */}
              <div
                style={{
                  border: `1px solid ${CONTROL_BORDER}`,
                  borderRadius: "4px",
                  maxHeight: "220px",
                  overflowY: "auto",
                }}
              >
                {discardSummary.rows.map((row, index) => (
                  <div
                    key={row.path}
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: "10px",
                      padding: "6px 10px",
                      borderTop: index === 0 ? "none" : `1px solid ${SECTION_DIVIDER}`,
                    }}
                  >
                    <Typography
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: "12px",
                        color: "#e6e6e6",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {row.name}
                    </Typography>
                    <Typography
                      style={{ fontSize: "11px", color: row.missing ? "#e0b96a" : "#8a8d93", flexShrink: 0 }}
                    >
                      {row.detail}
                    </Typography>
                  </div>
                ))}
              </div>

              <Typography style={{ fontSize: "12px", color: "#8a8d93", marginTop: "10px", lineHeight: 1.6 }}>
                {discardSummary.total ? `${discardSummary.total} ` : ""}
                {discardSummary.rows.length === 1 ? "It moves" : "They move"} to the library&apos;s{" "}
                <strong>badones</strong> folder — not deleted, but not reachable from here either.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPrompt(null)} disabled={working}>
                Cancel
              </Button>
              <Button
                color="secondary"
                disabled={working || discardSummary.movable === 0}
                onClick={() => void discardMarked()}
              >
                Discard
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Dialog>
  );
}

DialogAssetLibrary.propTypes = {
  baseID: PropTypes.string,
  assetType: PropTypes.string,
  multiple: PropTypes.bool,
  // Whether this user may add to the library. Absent means yes — the callers
  // that know about permissions pass their own answer.
  canWrite: PropTypes.bool,
  createSnackbar: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onChoose: PropTypes.func.isRequired,
  // Open here instead of wherever the library was last left — the Assets tab
  // passes the folder the asset being replaced actually came from. Absent (not
  // just falsy: "" is the root and a legitimate folder) falls back to the
  // shared last-visited folder.
  initialDirPath: PropTypes.string,
};
